import React, { useEffect, useMemo, useState } from 'react'
import { DayPilot, DayPilotCalendar, DayPilotMonth, DayPilotNavigator } from '@daypilot/daypilot-lite-react'
import './Calendar.css'
import { addObject, getObjects, editObject, deleteObject } from '../../api/apiClientCrud'
import { useAuth } from '../../context/AuthContext'
import EventDialog from '../dialogs/EventDialog'
import { toast } from 'react-toastify'
import { Box } from '@mui/material'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)

const toUTCString = (date) => dayjs(date).utc().toISOString()

const toLocalDayjs = (date) => dayjs.utc(date).local()

const toDayPilotString = (d) => d.format('YYYY-MM-DDTHH:mm:ss')

const toFormString = (s) => s.replace('T', ' ')

const Calendar = () => {
  const [view, setView] = useState('Week')
  const [startDate, setStartDate] = useState(DayPilot.Date.today())
  const [events, setEvents] = useState([])
  const [open, setOpen] = useState(false)
  const [event, setEvent] = useState({})
  const [cancel, setCancel] = useState(false)
  const [calendar, setCalendar] = useState(null)
  const { user } = useAuth()

  const config = useMemo(
    () => ({
      contextMenu: new DayPilot.Menu({
        items: [
          {
            text: 'Supprimer',
            onClick: (args) => calendar?.events.remove(args.source)
          },
          {
            text: 'Modifier',
            onClick: (args) => updateEvent(args.source)
          }
        ]
      })
    }),
    [calendar]
  )

  const onTimeRangeSelected = async (args) => {
    const dp = args.control
    setEvent({
      start: toFormString(args.start.value),
      end: toFormString(args.end.value)
    })
    setOpen(true)
    dp.clearSelection()
    if (cancel) setCancel(false)
  }

  const onBeforeEventRender = (args) => {
    const e = args.data
    e.html = `
      ${e.title ? `<div>${e.title}</div>` : ''}
      ${e.description ? `<div>${e.description}</div>` : ''}
    `
    if (e.tags?.type === 'important') e.barColor = 'red'

    e.areas = [
      {
        top: 3,
        right: 3,
        width: 20,
        height: 20,
        symbol: 'icons/daypilot.svg#minichevron-down-2',
        fontColor: '#fff',
        toolTip: 'Menu',
        action: 'ContextMenu'
      },
      {
        top: 3,
        right: 25,
        width: 20,
        height: 20,
        symbol: 'icons/daypilot.svg#x-circle',
        fontColor: '#fff',
        toolTip: 'Supprimer',
        action: 'None',
        onClick: async (args) => {
          try {
            await deleteObject('event', args.source.data.id)
            calendar.events.remove(args.source)
          } catch (e) {
            toast.error(e.response?.statusText || e.message, { autoClose: 2000 })
          }
        }
      }
    ]
  }

  const onEventMoved = (args) => {
    const e = args.e.data
    editObject('event', e.id, {
      ...e,
      start: toUTCString(e.start),
      end: toUTCString(e.end),
      lastModBy: user.email
    }).catch((err) => toast.error(err.response?.statusText || err.message, { autoClose: 2000 }))
  }

  const onEventClicked = (args) => {
    setEvent(args.e.data)
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)

    const payload = {
      ...event,
      start: toUTCString(event.start),
      end: toUTCString(event.end)
    }

    if (!event.id) {
      // Création
      addObject('event', {
        user_id: user.id,
        createdBy: user.email,
        ...payload
      })
        .then((res) => setEvents([...events, { id: res.data.id, ...event }]))
        .catch((err) => toast.error(err.response?.statusText || err.message, { autoClose: 2000 }))
    } else {
      // Modification
      const index = events.findIndex((ev) => ev.id === event.id)
      events[index] = { ...event, text: event.title }

      editObject('event', event.id, {
        ...payload,
        lastModBy: user.email
      })
        .then(() => setEvents([...events]))
        .catch((err) => toast.error(err.response?.statusText || err.message, { autoClose: 2000 }))
    }
  }

  const handleCancel = () => {
    setCancel(true)
    setOpen(false)
  }

  const updateEvent = (e) => {
    setEvent(e.data)
    setOpen(true)
  }

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const res = await getObjects('events', { user: user.id })
        const localEvents = res.data.events.map((e) => ({
          ...e,
          start: toDayPilotString(toLocalDayjs(e.start)),
          end: toDayPilotString(toLocalDayjs(e.end))
        }))
        setEvents(localEvents)
      } catch {
        toast.error('Erreur lors du chargement des événements', { autoClose: 2000 })
      }
    }
    fetchEvents()
  }, [user.id])

  const baseCalendarProps = {
    locale: 'fr-fr',
    startDate,
    events,
    ...config,
    durationBarVisible: false,
    onTimeRangeSelected,
    onBeforeEventRender,
    onEventClicked,
    onEventMoved,
    onEventResized: onEventMoved
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 65px)',
        overflowY: 'auto',
        flex: '1 1 auto'
      }}
    >
      <div className='container'>
        <div className='navigator'>
          <DayPilotNavigator
            selectMode={view}
            locale='fr-fr'
            showMonths={2}
            skipMonths={1}
            onTimeRangeSelected={(args) => setStartDate(args.day)}
            events={events}
          />
        </div>

        <div className='content'>
          <div className='toolbar' style={{ marginTop: '10px' }}>
            <div className='toolbar-group'>
              {['Day', 'Week', 'Month'].map((v) => (
                <button key={v} onClick={() => setView(v)} className={view === v ? 'selected' : ''}>
                  {v === 'Day' ? 'Jour' : v === 'Week' ? 'Semaine' : 'Mois'}
                </button>
              ))}
            </div>
            <button onClick={() => setStartDate(DayPilot.Date.today())} className='standalone'>
              Aujourd'hui
            </button>
          </div>

          {view === 'Day' && <DayPilotCalendar {...baseCalendarProps} viewType='Day' />}
          {view === 'Week' && <DayPilotCalendar {...baseCalendarProps} viewType='Week' controlRef={setCalendar} />}
          {view === 'Month' && <DayPilotMonth {...baseCalendarProps} eventBarVisible={false} />}
        </div>

        <EventDialog open={open} onClose={handleClose} onCancel={handleCancel} event={event} setEvent={setEvent} />
      </div>
    </Box>
  )
}

export default Calendar
